import { useEffect, useState } from 'react';
import { useToast } from '../Toast';
import { Button } from '../ui';
import { fetchDrawers, uploadDrawerDoorCover, setDrawerCoverImage } from '../../lib/queries';
import type { Drawer } from '../../lib/types';

export function DrawersPanel({ active }: { active: boolean }) {
  const toast = useToast();
  const [drawers, setDrawers] = useState<Drawer[]>([]);
  // Drawer whose door-image upload/remove is in flight (one at a time).
  const [doorBusy, setDoorBusy] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        setDrawers(await fetchDrawers());
      } catch (err: any) {
        console.error('[admin] drawers load failed', err);
        toast.error(err?.message ?? 'Failed to load drawers.');
      } finally {
        setLoaded(true);
      }
    }
    if (active && !loaded) void load();
  }, [active]);

  async function handleDoorCover(d: Drawer, file: File | null) {
    if (!file || doorBusy) return;
    setDoorBusy(d.id);
    try {
      const url = await uploadDrawerDoorCover({ drawerId: d.id, file });
      await setDrawerCoverImage(d.id, url);
      setDrawers((prev) => prev.map((x) => (x.id === d.id ? { ...x, cover_image_url: url } : x)));
      toast.success(`${d.name} door image set.`);
    } catch (err: any) {
      console.error('[admin] door cover upload failed', err);
      toast.error(err?.message ?? 'Upload failed');
    } finally {
      setDoorBusy(null);
    }
  }

  async function handleDoorCoverRemove(d: Drawer) {
    if (doorBusy) return;
    setDoorBusy(d.id);
    try {
      await setDrawerCoverImage(d.id, null);
      setDrawers((prev) => prev.map((x) => (x.id === d.id ? { ...x, cover_image_url: null } : x)));
      toast.success(`${d.name} door image removed.`);
    } catch (err: any) {
      console.error('[admin] door cover remove failed', err);
      toast.error(err?.message ?? 'Remove failed');
    } finally {
      setDoorBusy(null);
    }
  }

  return (
    <>
      <div className="admin__panel-head">
        <h2>Drawers</h2>
        <span className="admin__count">{drawers.length}</span>
      </div>
      <ul className="admin__drawers">
        {drawers.map((d) => (
          <li key={d.id} className="admin__drawer">
            <strong>{d.name}</strong>
            <code>{d.slug}</code>
            <div className="admin__drawer-door">
              {d.cover_image_url ? (
                <img src={d.cover_image_url} alt={`${d.name} door image`} />
              ) : (
                <span className="admin__drawer-door-empty">No door image</span>
              )}
              <div className="admin__drawer-door-actions">
                <label className={`btn btn--secondary btn--sm ${doorBusy === d.id ? 'btn--disabled' : ''}`}>
                  {doorBusy === d.id ? '…' : d.cover_image_url ? 'Replace' : 'Set door image'}
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    disabled={doorBusy === d.id}
                    onChange={(e) => {
                      void handleDoorCover(d, e.target.files?.[0] ?? null);
                      e.target.value = '';
                    }}
                  />
                </label>
                {d.cover_image_url && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => void handleDoorCoverRemove(d)}
                    disabled={doorBusy === d.id}
                  >
                    Remove
                  </Button>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>
      <p className="admin__note">
        A door image fronts that closet's doors on the feed instead of the
        latest post's media. Members see it through the open doors.
      </p>
    </>
  );
}
